/**
 * PromoAttacher — mounted once at the app root.
 *
 * When the URL contains `?p=<token>` (a discount-URL param), we quietly ping
 * `POST /api/promo/click/<token>` to record the click and check eligibility.
 * If the promo is still valid we save the token in `localStorage.promo_tokens`
 * (deduped, capped to 8) so `CartDrawer` can pass it to `POST /api/checkout`.
 *
 * We then strip the `p` param from the visible URL so the buyer never sees
 * anything unusual — the page behaves exactly like the plain product URL.
 * Invalid / expired promos are silently discarded (endpoint returns
 * `{valid: false}` with HTTP 200 so we don't leak info).
 */
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'promo_tokens';
const MAX_TOKENS = 8;

const readTokens = () => {
  try {
    const arr = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
};

const writeTokens = (tokens) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens.slice(-MAX_TOKENS)));
};

const PromoAttacher = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('p');
    if (!token || token.length < 6 || token.length > 20) return;

    // Fire-and-forget click; strip the param regardless so URL looks normal.
    axios
      .post(`${API}/promo/click/${encodeURIComponent(token)}`)
      .then((res) => {
        if (res?.data?.valid) {
          const cur = readTokens();
          if (!cur.includes(token)) writeTokens([...cur, token]);
        }
      })
      .catch(() => {
        /* silent — a promo failure MUST look identical to a plain page load */
      });

    // Clean the URL — remove `p` while preserving the rest of the query.
    params.delete('p');
    const nextSearch = params.toString();
    navigate(
      { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '', hash: location.hash },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  return null;
};

export default PromoAttacher;
